import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Play } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack } from '@components/base/layout';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { erpEndpointsService } from '../../api/erpEndpointsService';
import type { ErpEndpoint } from '../../api/erpEndpointsService';

// ---------------------------------------------------------------------------
// HTTP method badge colors
// ---------------------------------------------------------------------------

const METHOD_BADGE_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'error' | 'primary' | 'default'> = {
  GET: 'success',
  POST: 'info',
  PUT: 'warning',
  PATCH: 'warning',
  DELETE: 'error',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface EndpointsListPanelProps {
  readonly registryId: string;
}

export function EndpointsListPanel({ registryId }: EndpointsListPanelProps) {
  const navigate = useNavigate();

  const listPage = useAdminListPage<ErpEndpoint, string>({
    queryKey: ['admin', 'erp', 'registries', registryId, 'endpoints'],
    queryFn: () => erpEndpointsService.list(registryId),
    deleteFn: (endpointSlug) => erpEndpointsService.remove(registryId, endpointSlug),
    getDeleteId: (endpoint) => endpoint.slug,
    extraInvalidateKeys: [
      ['admin', 'erp', 'registries', registryId],
    ],
  });

  return (
    <AdminListPage<ErpEndpoint>
      variant="panel"
      title="Endpoints"
      loadingLabel="Loading endpoints..."
      errorTitle="Failed to load endpoints"
      errorFallback="An unexpected error occurred."
      emptyTitle="No endpoints found"
      emptyDescription="Get started by creating your first endpoint for this registry."
      createLabel="Create Endpoint"
      createButtonSize="sm"
      onCreateClick={() => navigate(`/admin/erp/registries/${registryId}/endpoints/create`)}
      items={listPage.items}
      getRowKey={(endpoint) => endpoint.id}
      onRowClick={(endpoint) => navigate(`/admin/erp/registries/${registryId}/endpoints/${endpoint.slug}/edit`)}
      isLoading={listPage.isLoading}
      error={listPage.error}
      deleteModalTitle="Delete Endpoint"
      deleteTarget={listPage.deleteTarget}
      getDeleteTargetName={(endpoint) => endpoint.name}
      deleteNoun="endpoint"
      onDeleteCancel={listPage.cancelDelete}
      onDeleteConfirm={listPage.confirmDelete}
      isDeleting={listPage.isDeleting}
      isDeleteError={listPage.isDeleteError}
      deleteError={listPage.deleteError}
      renderHead={() => (
        <>
          <Table.HeaderCell>Description</Table.HeaderCell>
          <Table.HeaderCell>Method</Table.HeaderCell>
          <Table.HeaderCell>Path</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
          <Table.HeaderCell align="right">Actions</Table.HeaderCell>
        </>
      )}
      renderRow={(endpoint) => (
        <>
          <Table.Cell>
            <Text size="sm" color="muted">
              {endpoint.description}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <Badge
              variant={METHOD_BADGE_VARIANT[endpoint.http_method] ?? 'default'}
              size="sm"
            >
              {endpoint.http_method}
            </Badge>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm" color="muted">
              {endpoint.path}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <Badge
              variant={endpoint.is_active ? 'success' : 'default'}
              size="sm"
            >
              {endpoint.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </Table.Cell>
          <Table.Cell align="right">
            <HStack gap={1} justify="end">
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon icon={Play} size="xs" color="inherit" />}
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/erp/registries/${registryId}/endpoints/${endpoint.slug}/dry-run`); }}
                aria-label={`Dry run ${endpoint.name}`}
              />
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon icon={Pencil} size="xs" color="inherit" />}
                onClick={(e) => { e.stopPropagation(); navigate(`/admin/erp/registries/${registryId}/endpoints/${endpoint.slug}/edit`); }}
                aria-label={`Edit ${endpoint.name}`}
              />
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<Icon icon={Trash2} size="xs" color="inherit" />}
                onClick={(e) => { e.stopPropagation(); listPage.setDeleteTarget(endpoint); }}
                aria-label={`Delete ${endpoint.name}`}
              />
            </HStack>
          </Table.Cell>
        </>
      )}
    />
  );
}
